import {
  Client,
  Conversation,
  Message,
  Paginator,
  Participant,
} from '@twilio/conversations';
import { useInfiniteQuery, useQueries, useQuery } from 'react-query';
import { useMemo } from 'react';
import {
  ChatConversation,
  ChatConversationType,
  ChatMessage,
  ChatParticipant,
} from '../types';
import {
  queryKeys,
  QueryKeyTypes,
  conversationToChatConversation,
  messageToChatMessage,
  participantToChatParticipant,
  userToChatUser,
} from '../cache';

export const useChatConversations = (
  userId: string,
  client: Client,
  type?: ChatConversationType,
) => {
  const process = useInfiniteQuery({
    queryKey: queryKeys.conversations(userId),
    queryFn: ({ pageParam }): Promise<Paginator<Conversation>> => {
      if (pageParam) return pageParam();
      return client.getSubscribedConversations();
    },
    getPreviousPageParam: (lastPage) => {
      if (lastPage?.hasPrevPage) return () => lastPage.prevPage();
      return undefined;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.hasNextPage) return () => lastPage.nextPage();
      return undefined;
    },
  });

  const conversations = useMemo<Array<ChatConversation>>(
    () =>
      process.data?.pages
        .flatMap((p) => p.items.map(conversationToChatConversation))
        .filter((c) => !type || c.attributes.type === type)
        .filter(
          (item, index, self) =>
            index === self.findIndex((obj) => obj.sid === item.sid),
        )
        .sort(
          (a, b) =>
            (b.instance.lastMessage?.dateCreated?.getTime() ||
              b.instance.dateCreated?.getTime() ||
              Date.now()) -
            (a.instance.lastMessage?.dateCreated?.getTime() ||
              a.instance.dateCreated?.getTime() ||
              Date.now()),
        ) || [],
    [process.data, type],
  );

  return {
    ...process,
    conversations,
  };
};

export const useConversationBySid = (
  userId: string,
  client: Client | undefined,
  sid: string,
) =>
  useQuery({
    queryKey: queryKeys.conversation(userId, sid),
    queryFn: client
      ? async (): Promise<QueryKeyTypes['conversation']> =>
          conversationToChatConversation(await client.getConversationBySid(sid))
      : undefined,
    enabled: !!client,
  });

function isConversationNotFoundError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return (
    message.includes('Not Found') ||
    (err as { name?: string })?.name === 'TwilsockUpstreamError'
  );
}

export const useConversationById = (
  userId: string,
  client: Client | undefined,
  conversationType: ChatConversationType,
  id: string,
) => {
  const uniqueName = `${conversationType}_${id}`;

  return useQuery({
    queryKey: queryKeys.conversationName(userId, uniqueName),
    queryFn: client
      ? async (): Promise<QueryKeyTypes['conversation'] | undefined> => {
          try {
            const conv = await client.getConversationByUniqueName(uniqueName);
            return conversationToChatConversation(conv);
          } catch (err) {
            if (isConversationNotFoundError(err)) {
              return undefined;
            }
            throw err;
          }
        }
      : undefined,
    enabled: !!client,
  });
};

export const useChatMessages = (
  userId: string,
  conversation: Conversation | undefined,
) => {
  const process = useInfiniteQuery({
    queryKey: conversation ? queryKeys.messages(userId, conversation.sid) : [],
    queryFn: conversation
      ? async ({
          pageParam,
        }): Promise<QueryKeyTypes['messages']['pages'][number]> => {
          const paginator = pageParam
            ? await (pageParam as typeof conversation.getMessages)()
            : await conversation.getMessages(100, undefined, 'backwards');
          const messages = await Promise.all(
            paginator.items.map(messageToChatMessage),
          );
          return { messages, paginator };
        }
      : undefined,
    getPreviousPageParam: (lastPage) => {
      if (lastPage?.paginator.hasPrevPage)
        return () => lastPage.paginator.prevPage();
      return undefined;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.paginator.hasNextPage)
        return () => lastPage.paginator.nextPage();
      return undefined;
    },
    staleTime: 1,
    cacheTime: 1,
    enabled: !!conversation,
  });

  const messages = useMemo<Array<ChatMessage>>(
    () =>
      process.data?.pages
        .flatMap((p) => p.messages)
        .filter(
          (item, index, self) =>
            index === self.findIndex((obj) => obj.sid === item.sid),
        )
        .sort(
          (a, b) =>
            (a.instance.dateCreated?.getTime() || Date.now()) -
            (b.instance.dateCreated?.getTime() || Date.now()),
        ) || [],
    [process.data],
  );

  return {
    ...process,
    messages,
  };
};

export const useMessage = (userId: string, message: Message) =>
  useQuery({
    queryKey: queryKeys.message(userId, message.conversation.sid, message.sid),
    queryFn: () => messageToChatMessage(message),
  });

export const useMessageFiles = (userId: string, message: Message) =>
  useQuery({
    queryKey: queryKeys.messageFiles(
      userId,
      message.conversation.sid,
      message.sid,
    ),
    queryFn: async (): Promise<QueryKeyTypes['messageFiles']> =>
      Promise.all(
        message.attachedMedia?.map(async (media) => ({
          name: media.filename || 'unknown',
          type: media.contentType,
          url: (await media.getContentTemporaryUrl()) || '',
        })) || [],
      ),
  });

export const useLastMessage = (
  userId: string,
  conversation: Conversation | undefined,
) =>
  useQuery({
    queryKey: conversation
      ? queryKeys.lastMessage(userId, conversation.sid)
      : [],
    queryFn: conversation
      ? async (): Promise<QueryKeyTypes['lastMessage']> => {
          const res = await conversation.getMessages(1);
          return res.items.length
            ? messageToChatMessage(res.items[0])
            : undefined;
        }
      : undefined,
    enabled: !!conversation,
  });

export const useParticipants = (userId: string, conversation: Conversation) => {
  const process = useQuery({
    queryKey: queryKeys.participants(userId, conversation.sid),
    queryFn: (): Promise<QueryKeyTypes['participants']> =>
      conversation.getParticipants(),
  });

  const participants = useMemo<Array<ChatParticipant>>(
    () => process.data?.map(participantToChatParticipant) || [],
    [process.data],
  );

  return {
    ...process,
    participants,
  };
};

export const useConversationUsers = (
  userId: string,
  conversation: Conversation,
) => {
  const participantsProcess = useParticipants(userId, conversation);

  return useQueries(
    participantsProcess.participants.map((participant) => ({
      queryKey: queryKeys.user(userId, participant.identity),
      queryFn: async (): Promise<QueryKeyTypes['user']> =>
        userToChatUser(await participant.instance.getUser()),
    })),
  );
};

export const useParticipantFromMessage = (
  userId: string,
  message: Message | undefined | null,
) =>
  useQuery({
    queryKey: queryKeys.participant(
      userId,
      message?.conversation.sid,
      message?.participantSid,
    ),
    queryFn: message
      ? async (): Promise<QueryKeyTypes['participant']> =>
          participantToChatParticipant(await message.getParticipant())
      : undefined,
    enabled: !!message,
  });

export const useUserFromParticipant = (
  userId: string,
  participant: Participant | undefined | null,
) =>
  useQuery({
    queryKey: queryKeys.user(userId, participant?.identity),
    queryFn: participant
      ? async (): Promise<QueryKeyTypes['user']> =>
          userToChatUser(await participant.getUser())
      : undefined,
    enabled: !!participant?.identity,
  });

export const useUser = (
  client: Client | undefined,
  userId: string,
  identity: string,
) =>
  useQuery({
    queryKey: queryKeys.user(userId, identity),
    queryFn: client
      ? async (): Promise<QueryKeyTypes['user']> =>
          userToChatUser(await client.getUser(identity))
      : undefined,
    enabled: !!client,
  });
